'use client';
import React, {createRef, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {PayPalMessages} from '@paypal/react-paypal-js';
import cx from 'classnames';

import Spinner from '@stubhub/react-spinner';

import '../styles/index.scss';

const MESSAGE_TIMEOUT = 1000;

/** Renders the pay later PayPal message, hidding it if there's some error from Paypal side */
const PayLaterMessage = ({amount, buyerCountry, className, isLoading, forceReRender = [], textAlign = 'left'}) => {
  const [hasError, setError] = useState(false);
  const [showLoading, setShowLoading] = useState(isLoading);
  const timeout = createRef(null);

  useEffect(() => {
    // We add this to be able to hide the block in case the message load fails
    if (!isLoading) {
      timeout.current = setTimeout(() => {
        setError(true);
        setShowLoading(false);
      }, MESSAGE_TIMEOUT);
    }

    return () => {
      clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const onRender = ({meta} = {}) => {
    clearTimeout(timeout.current);
    const hasError = Object.keys(meta).length === 0;
    setShowLoading(false);
    setError(hasError);
  };

  return (
    <div className={cx('paylater__container', {'paylater__container--error': !isLoading && hasError}, className)}>
      {showLoading && <Spinner />}
      {!isLoading && (
        <PayPalMessages
          amount={amount || 0}
          buyerCountry={buyerCountry}
          forceReRender={forceReRender}
          onRender={onRender}
          style={{text: {align: textAlign}}}
          data-testid="paylater__message"
          className="paylater__message"
        />
      )}
    </div>
  );
};

PayLaterMessage.propTypes = {
  /** If it's still loading we will show a spinner */
  isLoading: PropTypes.bool,

  /** The amount to show in pay later message. If not provided, the message will be shown without amount */
  amount: PropTypes.number,

  /** The buyer country to show the paylater message */
  buyerCountry: PropTypes.string.isRequired,

  /** Custom classname */
  className: PropTypes.string,

  /** List of dependencies to re-render the PayPal messages component */
  forceReRender: PropTypes.array,

  /** The alignment for pay later message */
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
};

export default PayLaterMessage;
